import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { Control, Controller } from 'react-hook-form';
import { cn } from '../../../../lib/utils';
import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '../../atoms';

interface SingleDatePickerProps {
  control: Control<any>;
  inputDisabled?: boolean;
  calendarDisabled?: boolean;
  name: string;
  label?: string | React.ReactNode;
  placeholder?: string;
  dateFormat?: string;
  startDate?: Date;
  endDate?: Date;
  onChange?: (date: Date | null) => void;
  disabled?: boolean;
  disabledDate?: (date: Date) => boolean;
}

export const SingleDatePicker = ({
  control,
  inputDisabled,
  calendarDisabled,
  name,
  label,
  placeholder,
  dateFormat = 'dd/MM/yyyy',
  startDate,
  endDate,
  disabled,
  disabledDate
}: SingleDatePickerProps) => {
  const isDateDisabled = (date: Date) => {
    if (disabled || calendarDisabled) return true;

    if (disabledDate && disabledDate(date)) return true;

    const currentDate = new Date(date);
    currentDate.setHours(0, 0, 0, 0);

    if (startDate) {
      const minDate = new Date(startDate);
      minDate.setHours(0, 0, 0, 0);
      if (currentDate < minDate) return true;
    }

    if (endDate) {
      const maxDate = new Date(endDate);
      maxDate.setHours(0, 0, 0, 0);
      if (currentDate > maxDate) return true;
    }

    return false;
  };

  return (
    <div>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <div className='flex flex-col gap-2'>
            <Popover>
              {label && (
                <span className='text-sm font-medium leading-none'>
                  {label}
                </span>
              )}
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className={cn(
                    'w-full justify-start text-left font-normal shadow-input rounded-lg',
                    !field.value && 'text-muted-foreground',
                    error && 'border-destructive'
                  )}
                  disabled={disabled || inputDisabled}
                >
                  <CalendarIcon className='mr-2 h-4 w-4' />
                  {field.value ? (
                    format(field.value, dateFormat)
                  ) : (
                    <span>{placeholder}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-auto p-0' align='start'>
                <Calendar
                  mode='single'
                  selected={field.value}
                  onSelect={field.onChange}
                  disabled={isDateDisabled}
                  initialFocus
                  fromDate={startDate}
                  toDate={endDate}
                />
              </PopoverContent>
            </Popover>
            {error && (
              <span className='text-sm font-medium text-destructive'>
                {error.message}
              </span>
            )}
          </div>
        )}
      />
    </div>
  );
};
